<script setup lang="ts">
import FlatRouterLinkButton from '@/components/atoms/FlatRouterLinkButton.vue'
import { RouterLink, useRoute } from 'vue-router'
import {
  useDeleteScenarioChatMutation,
  useScenarioChatIndexPageQuery,
} from './ScenarioChatIndexPage.generated'
import ButtonIconOnly from '@/components/atoms/ButtonIconOnly.vue'
import IconDustBox from '@/components/icons/IconDustBox.vue'
import { useAlert } from '@/libs/alertPortal'
import * as Sentry from '@sentry/vue'

const alert = useAlert()
const route = useRoute('scenarioChatIndex')

const pageQuery = useScenarioChatIndexPageQuery({
  fetchPolicy: 'no-cache',
})

const deleteScenarioChatMutation = useDeleteScenarioChatMutation()

const handleClickDeleteButton = async (guid: string) => {
  const confirmed = window.confirm('本当に削除しますか？')

  if (confirmed) {
    try {
      const result = await deleteScenarioChatMutation.mutate({ guid })
      if (result == null || result.data == null) {
        throw new Error('result is null')
      }

      switch (result.data.deleteScenarioChatV2.__typename) {
        case 'DeleteScenarioChatErrorAssociatedWithIntroductionMessage':
          alert.error(
            '削除できませんでした。\nこのシナリオチャットは、初期チャットメッセージとして設定されているため削除できません。\n削除するには初期チャットメッセージでの設定を解除してください。'
          )
          return
        case 'DeleteScenarioChatSuccess':
          alert.success('削除しました')
          pageQuery.refetch()
          return
        default:
          throw new Error('unknown error')
      }
    } catch (error) {
      console.error(error)
      Sentry.captureException(error)
      alert.error('削除に失敗しました')
    }
  }
}
</script>

<template>
  <section :class="$style.container">
    <header :class="$style.header">
      <h1>シナリオチャット</h1>
      <FlatRouterLinkButton
        variant="primary"
        size="medium"
        :to="{ name: 'scenarioChatNew' }"
      >
        新規作成
      </FlatRouterLinkButton>
    </header>
    <hr :class="$style.hr" />
    <div :class="$style.containerInner">
      <table :class="$style.table">
        <colgroup>
          <col />
          <col :class="$style.deleteColumn" />
        </colgroup>
        <thead>
          <tr>
            <th>タイトル</th>
            <th></th>
          </tr>
        </thead>
        <tbody
          v-if="
            pageQuery.result.value &&
            pageQuery.result.value.scenarioChats.scenarioChats.length > 0
          "
        >
          <tr
            v-for="scenarioChat of pageQuery.result.value?.scenarioChats
              .scenarioChats"
            :key="scenarioChat.guid"
          >
            <td>
              <RouterLink
                :to="{
                  name: 'scenarioChatEdit',
                  params: {
                    entranceGUID: route.params.entranceGUID,
                    guid: scenarioChat.guid,
                  },
                }"
                >{{ scenarioChat.title }}</RouterLink
              >
            </td>
            <td :class="$style.deleteColumn">
              <ButtonIconOnly
                :class="$style.deleteButton"
                @click="handleClickDeleteButton(scenarioChat.guid)"
                title="削除する"
              >
                <template #icon>
                  <IconDustBox />
                </template>
              </ButtonIconOnly>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="6" :class="$style.none">
              シナリオチャットがありません
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<style module lang="scss">
.container {
  background-color: $color-surface-primary;
  padding: 16px;
  border-radius: 10px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.hr {
  border: none;
  border-top: 1px solid $color-border-low-emphasis;
  margin: 16px 0;
}

.containerInner {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.table {
  border-collapse: collapse;

  thead {
    background-color: $color-background-base;

    th {
      padding: 12px;
      text-align: left;
    }
  }

  .deleteColumn {
    width: 44px;
  }

  .deleteColumnTD {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .deleteButton {
    &:hover {
      color: $color-caution-accent;
    }
  }

  tbody {
    tr {
      &:not(:first-child) {
        border-top: 1px solid $color-border-low-emphasis;
      }

      td {
        padding: 24px 12px;

        a {
          display: block;
          color: inherit;
          text-decoration: underline;
        }
      }
    }
  }
}

.none {
  text-align: center;
}
</style>
